import { useMemo, useState } from "react"
import { useFormik } from "formik"
import * as Yup from "yup"
import { useQuery } from "@tanstack/react-query"
import {
  Button,
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap"
import Select from "react-select"
import { toast } from "react-toastify"

import InputField from "components/Common/InputField"
import FormikCreatableSelect from "components/Common/FormikCreatableSelect"
import ToggleButton from "components/Common/ToggleButton"
import { getUsers } from "api/userApi"
import {
  useCreatePrayerReminderSetting,
  useEditPrayerReminderSetting,
  usePrayerReminderMeta,
} from "hooks/prayerReminderHook"

const PRAYERS = ["fajr", "dhuhr", "asr", "maghrib", "isha"]
const PRAYER_LABELS = {
  fajr: "Fajr",
  dhuhr: "Dhuhr",
  asr: "Asr",
  maghrib: "Maghrib",
  isha: "Isha",
}

const emptyPrayers = () =>
  PRAYERS.reduce(
    (acc, key) => ({
      ...acc,
      [key]: {
        enabled: true,
        offset_minutes: 0,
        jamaat_enabled: false,
        jamaat_after_minutes: 20,
        jamaat_lead_minutes: 5,
      },
    }),
    {}
  )

const toOptions = (list = []) => list.map(v => ({ value: v, label: v }))

const validationSchema = Yup.object({
  showroom_id: Yup.mixed().required("Select a showroom"),
  target_type: Yup.string().oneOf(["all", "permission", "users"]).required(),
  target_permission: Yup.string().when("target_type", {
    is: "permission",
    then: schema => schema.required("Select a permission"),
  }),
  target_user_ids: Yup.array().when("target_type", {
    is: "users",
    then: schema => schema.min(1, "Select at least one user"),
  }),
})

const PrayerReminderSettingModal = ({ isOpen, toggle, setting }) => {
  const isEdit = Boolean(setting?.id)
  const { data: metaResp } = usePrayerReminderMeta(isOpen)
  const meta = metaResp?.data ?? {}
  const [showAdvanced, setShowAdvanced] = useState(false)

  const createMutation = useCreatePrayerReminderSetting()
  const editMutation = useEditPrayerReminderSetting()

  const { data: usersResp } = useQuery({
    queryKey: ["prayer-reminder-user-options"],
    queryFn: () => getUsers({ per_page: 200 }),
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
  })

  const userOptions = useMemo(
    () =>
      (usersResp?.data?.data ?? usersResp?.data ?? []).map(u => ({
        value: u.id,
        label: u.name ? `${u.name}${u.email ? ` (${u.email})` : ""}` : `#${u.id}`,
      })),
    [usersResp]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      showroom_id: setting?.showroom_id ?? "",
      active: setting?.active ?? true,
      prayers: { ...emptyPrayers(), ...(setting?.prayers ?? {}) },
      skip_friday_dhuhr: setting?.skip_friday_dhuhr ?? false,
      title_en: setting?.title_en ?? "",
      title_bn: setting?.title_bn ?? "",
      body_en: setting?.body_en ?? "",
      body_bn: setting?.body_bn ?? "",
      target_type: setting?.target_type ?? "permission",
      target_permission: setting?.target_permission ?? "receive-prayer-reminders",
      target_user_ids: setting?.target_user_ids ?? [],
    },
    validationSchema,
    onSubmit: async values => {
      const payload = {
        showroom_id: values.showroom_id,
        active: values.active,
        prayers: PRAYERS.reduce((acc, key) => {
          const p = values.prayers[key] ?? {}
          return {
            ...acc,
            [key]: {
              enabled: Boolean(p.enabled),
              offset_minutes: Number(p.offset_minutes) || 0,
              jamaat_enabled: Boolean(p.jamaat_enabled),
              jamaat_after_minutes: Number(p.jamaat_after_minutes) || 0,
              jamaat_lead_minutes: Number(p.jamaat_lead_minutes) || 0,
            },
          }
        }, {}),
        skip_friday_dhuhr: values.skip_friday_dhuhr,
        title_en: values.title_en || null,
        title_bn: values.title_bn || null,
        body_en: values.body_en || null,
        body_bn: values.body_bn || null,
        target_type: values.target_type,
        target_permission:
          values.target_type === "permission" ? values.target_permission : null,
        target_user_ids:
          values.target_type === "users" ? values.target_user_ids : null,
      }

      try {
        if (isEdit) {
          await editMutation.mutateAsync({ id: setting.id, ...payload })
          toast.success("Prayer reminder updated")
        } else {
          await createMutation.mutateAsync(payload)
          toast.success("Prayer reminder created")
        }
        toggle()
      } catch (err) {
        toast.error(err?.response?.data?.message || "Save failed")
      }
    },
  })

  const { values, errors, touched, handleChange, setFieldValue } = formik

  const setPrayer = (key, patch) =>
    setFieldValue("prayers", {
      ...values.prayers,
      [key]: { ...values.prayers[key], ...patch },
    })

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" scrollable>
      <ModalHeader toggle={toggle}>
        {isEdit ? "Edit" : "Add"} Prayer Reminder
      </ModalHeader>
      <form
        onSubmit={formik.handleSubmit}
        className="d-flex flex-column flex-grow-1"
        style={{ minHeight: 0 }}
      >
        <ModalBody style={{ minHeight: 0, overflowY: "auto" }}>
          <Row>
            <Col md={6}>
              <FormikCreatableSelect
                label="Showroom *"
                name="showroom_id"
                formik={formik}
                options={(meta.showrooms ?? []).map(s => ({
                  value: s.id,
                  label: s.name,
                }))}
              />
              {touched.showroom_id && errors.showroom_id && (
                <div className="text-danger small mb-2">{errors.showroom_id}</div>
              )}
            </Col>
            <Col md={6} className="d-flex align-items-end">
              <div className="d-flex gap-2 mb-3">
                <ToggleButton
                  active={values.active}
                  onClick={() => setFieldValue("active", !values.active)}
                >
                  {values.active ? "Active" : "Inactive"}
                </ToggleButton>
                <ToggleButton
                  active={values.skip_friday_dhuhr}
                  color="warning"
                  onClick={() =>
                    setFieldValue("skip_friday_dhuhr", !values.skip_friday_dhuhr)
                  }
                >
                  {values.skip_friday_dhuhr
                    ? "Skips Friday Dhuhr"
                    : "Runs Friday Dhuhr"}
                </ToggleButton>
              </div>
            </Col>
          </Row>

          <label className="form-label fw-medium">
            Prayers — waqt start &amp; jamaat reminders
          </label>
          <div className="d-flex flex-column gap-2 mb-3">
            {PRAYERS.map(key => {
              const p = values.prayers[key] ?? {}
              return (
                <div key={key} className="border rounded p-2">
                  <Row className="align-items-center g-2">
                    <Col xs={12} md={4}>
                      <ToggleButton
                        active={p.enabled}
                        onClick={() => setPrayer(key, { enabled: !p.enabled })}
                      >
                        {p.enabled
                          ? `${PRAYER_LABELS[key]} — waqt reminder on`
                          : `${PRAYER_LABELS[key]} — waqt reminder off`}
                      </ToggleButton>
                    </Col>
                    <Col xs={12} md={8} className="d-flex align-items-center gap-2">
                      <InputField
                        type="number"
                        min={-120}
                        max={120}
                        disabled={!p.enabled}
                        value={p.offset_minutes}
                        onChange={e =>
                          setPrayer(key, { offset_minutes: e.target.value })
                        }
                      />
                      <small className="text-muted text-nowrap">
                        min from azan (− before / + after)
                      </small>
                    </Col>
                  </Row>

                  <Row className="align-items-center g-2 mt-1 pt-1 border-top">
                    <Col xs={12} md={4}>
                      <ToggleButton
                        color="info"
                        active={p.jamaat_enabled}
                        onClick={() =>
                          setPrayer(key, { jamaat_enabled: !p.jamaat_enabled })
                        }
                      >
                        {p.jamaat_enabled ? "Jamaat reminder on" : "Jamaat reminder off"}
                      </ToggleButton>
                    </Col>
                    <Col xs={6} md={4} className="d-flex align-items-center gap-2">
                      <InputField
                        type="number"
                        min={0}
                        max={180}
                        disabled={!p.jamaat_enabled}
                        value={p.jamaat_after_minutes}
                        onChange={e =>
                          setPrayer(key, { jamaat_after_minutes: e.target.value })
                        }
                      />
                      <small className="text-muted text-nowrap">min jamaat after azan</small>
                    </Col>
                    <Col xs={6} md={4} className="d-flex align-items-center gap-2">
                      <InputField
                        type="number"
                        min={0}
                        max={60}
                        disabled={!p.jamaat_enabled}
                        value={p.jamaat_lead_minutes}
                        onChange={e =>
                          setPrayer(key, { jamaat_lead_minutes: e.target.value })
                        }
                      />
                      <small className="text-muted text-nowrap">min remind before jamaat</small>
                    </Col>
                  </Row>
                </div>
              )
            })}
          </div>

          <Row>
            <Col md={4}>
              <FormikCreatableSelect
                label="Target"
                name="target_type"
                formik={formik}
                options={toOptions(
                  meta.target_types ?? ["all", "permission", "users"]
                )}
              />
            </Col>
            {values.target_type === "permission" && (
              <Col md={8}>
                <FormikCreatableSelect
                  label="Recipient permission"
                  name="target_permission"
                  formik={formik}
                  options={(meta.notification_permissions ?? []).map(p => ({
                    value: p.slug,
                    label: p.name,
                  }))}
                />
              </Col>
            )}
            {values.target_type === "users" && (
              <Col md={8}>
                <label className="form-label">Users</label>
                <Select
                  isMulti
                  options={userOptions}
                  value={userOptions.filter(o =>
                    values.target_user_ids.includes(o.value)
                  )}
                  onChange={selected =>
                    setFieldValue(
                      "target_user_ids",
                      (selected ?? []).map(o => o.value)
                    )
                  }
                />
                {touched.target_user_ids && errors.target_user_ids && (
                  <div className="text-danger small">
                    {errors.target_user_ids}
                  </div>
                )}
              </Col>
            )}
          </Row>

          <Button
            type="button"
            color="link"
            className="px-0"
            onClick={() => setShowAdvanced(v => !v)}
          >
            {showAdvanced ? "Hide" : "Show"} message override
          </Button>

          {showAdvanced && (
            <>
              <p className="text-muted small mb-2">
                Tokens <code>:prayer</code> (auto-localized prayer name) and{" "}
                <code>:time</code>. Leave a language blank to use the built-in
                default for that language.
              </p>
              <Row>
                <Col md={6}>
                  <label className="form-label">Title (English)</label>
                  <InputField
                    name="title_en"
                    value={values.title_en}
                    onChange={handleChange}
                    placeholder=":prayer time"
                  />
                </Col>
                <Col md={6}>
                  <label className="form-label">Title (Bangla)</label>
                  <InputField
                    name="title_bn"
                    value={values.title_bn}
                    onChange={handleChange}
                    placeholder=":prayer এর সময়"
                  />
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <label className="form-label">Message (English)</label>
                  <InputField
                    type="textarea"
                    name="body_en"
                    value={values.body_en}
                    onChange={handleChange}
                    placeholder="It's time for :prayer (:time)"
                  />
                </Col>
                <Col md={6}>
                  <label className="form-label">Message (Bangla)</label>
                  <InputField
                    type="textarea"
                    name="body_bn"
                    value={values.body_bn}
                    onChange={handleChange}
                    placeholder=":prayer এর সময় হয়েছে (:time)"
                  />
                </Col>
              </Row>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" type="button" onClick={toggle}>
            Cancel
          </Button>
          <Button
            color="primary"
            type="submit"
            disabled={createMutation.isPending || editMutation.isPending}
          >
            {editMutation.isPending || createMutation.isPending
              ? "Saving..."
              : isEdit
                ? "Update"
                : "Create"}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  )
}

export default PrayerReminderSettingModal
